import { useAuth } from '@entities/auth'
import { useConfirmQuery } from '@entities/payment'
import { ProductSummarySection } from '@features/payment'
import { LinkSaveCard } from '@features/payment/ui/link-save-card'
import {
  PaymentConfirmComplete,
  PaymentConfirmError,
  PaymentConfirmLoading,
} from '@features/payment/ui/payment-success-fallbacks'
import { PosthogPageView } from '@shared/lib/posthog'
import { Sentry } from '@shared/lib/sentry'
import { Button } from '@shared/ui/button'
import { NoSsrSuspense } from '@shared/ui/no-ssr-suspense'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ErrorBoundary } from 'react-error-boundary'

export const Route = createFileRoute('/payment-success')({
  validateSearch: (search: { paymentKey?: string; orderId?: string; amount?: string }) => {
    const paymentKey = search.paymentKey
    const orderId = search.orderId
    const amount = Number(search.amount)
    if (
      typeof paymentKey !== 'string' ||
      typeof orderId !== 'string' ||
      Number.isNaN(amount) ||
      amount <= 0
    ) {
      throw new Error('결제 정보가 올바르지 않아요.')
    }
    return { paymentKey, orderId, amount }
  },
  component: () => {
    const { orderId } = Route.useSearch()

    return (
      <PosthogPageView event="view_payment_success">
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary
              onReset={reset}
              onError={(error) => {
                Sentry.captureException(error, {
                  tags: { feature: 'payment-confirm' },
                  extra: { orderId },
                })
              }}
              fallbackRender={({ resetErrorBoundary }) => (
                <PaymentConfirmError orderId={orderId} onReset={resetErrorBoundary} />
              )}
            >
              <NoSsrSuspense fallback={<PaymentConfirmLoading />}>
                <PaymentSuccessPage />
              </NoSsrSuspense>
            </ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </PosthogPageView>
    )
  },
  ssr: false,
})

function PaymentSuccessPage() {
  const { paymentKey, orderId, amount } = Route.useSearch()
  const { isLoggedIn } = useAuth()
  const { data } = useConfirmQuery({ paymentKey, orderId, amount })

  return (
    <section className="flex flex-1 flex-col items-center justify-center gap-6 px-6">
      <section className="flex-1 pt-16">
        <PaymentConfirmComplete amount={amount} />

        <ProductSummarySection orderName={'사주 상세 분석'} amount={amount} />
      </section>

      <section className="w-full space-y-4 pb-content-bottom">
        {!isLoggedIn && <LinkSaveCard />}

        {data.hashKey && (
          <Button asChild variant="primary" size="lg" className="w-full">
            <Link
              to="/saju/$sajuType/result/detail"
              params={{ sajuType: 'classic' }}
              search={{ hashKey: data.hashKey }}
            >
              결과 보러가기
            </Link>
          </Button>
        )}
      </section>
    </section>
  )
}
